import { useState, useEffect } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useSuburbSearch } from "../../../hooks/use-suburbs";

interface Suburb {
  id: string;
  name: string;
  region: string | null;
  displayName: string;
  suburbId: number;
}

export interface SuburbAutocompleteMuiProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

export function SuburbAutocompleteMui({
  label,
  value,
  onChange,
  placeholder,
  required,
  error,
  helperText,
  disabled,
}: SuburbAutocompleteMuiProps) {
  const [inputValue, setInputValue] = useState(value);
  const [open, setOpen] = useState(false);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const {
    data: suburbs = [],
    isLoading,
    isError,
    error: searchError,
  } = useSuburbSearch(inputValue);

  // Debug: Log API errors
  if (isError) {
    console.error("[SuburbAutocomplete] Search error:", searchError);
  }

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: string | Suburb | null,
  ) => {
    if (typeof newValue === "string") {
      onChange(newValue);
    } else if (newValue) {
      onChange(newValue.displayName);
    } else {
      onChange("");
    }
  };

  const handleInputChange = (
    _event: React.SyntheticEvent,
    newInputValue: string,
  ) => {
    setInputValue(newInputValue);
  };

  return (
    <Autocomplete
      freeSolo
      open={open && inputValue.length >= 2}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={suburbs}
      loading={isLoading && !isError}
      disabled={disabled}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.displayName
      }
      noOptionsText={isError ? "Error loading suburbs" : "No suburbs found"}
      filterOptions={(x) => x} // Disable client-side filtering - server handles it
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        // eslint-disable-next-line react/prop-types
        const { key, ...otherProps } = props;
        return (
          <Box component="li" key={key} {...otherProps}>
            <Box>
              <Typography variant="body1">{option.name}</Typography>
              {option.region && (
                <Typography variant="body2" color="text.secondary">
                  {option.region}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
    />
  );
}
